import React, { useContext } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Button,
  Container,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import AuthContext from '../../../contexts/AuthContext';

const product = {
  name: 'VeraBIND Tau',
  image: '/kit_0001.png',
  imageAlt: 'VeraBIND Tau kit',
};

export default function CustomerPurchaseFlow() {
  const { tokens, user } = useContext(AuthContext);
  const isLoggedIn = Boolean(tokens?.id_token);
  const displayName = user?.email || user?.name || 'your account';
  const steps = [
    {
      title: 'Sign in or create your account',
      description: isLoggedIn
        ? `Completed - Signed in as ${displayName}.`
        : 'We will confirm whether you are already logged in or help you create a secure account to continue.',
    },
    {
      title: 'Complete the intake questions',
      description:
        'Provide basic demographics and a short health questionnaire to guide the ordering process.',
    },
    {
      title: 'Assign a provider and confirm test fit',
      description:
        'A licensed provider will review your information to determine whether this test is appropriate.',
    },
    {
      title: 'Confirm shipping details',
      description:
        'Verify the kit delivery address and preferred shipping options before purchase.',
    },
    {
      title: 'Complete purchase',
      description: 'Finish checkout to place your kit order.',
    },
  ];

  return (
    <Container disableGutters sx={{ mt: 0 }}>
      <Box
        sx={{
          maxWidth: 'md',
          mx: 'auto',
          textAlign: 'left',
          pt: { xs: 0.5, md: 0.5 },
          pb: { xs: 0.5, md: 0.5 },
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
            <Box
              sx={{
                border: '1px solid #97999B',
                borderRadius: 2,
                p: { xs: 2, md: 2.5 },
                flex: 1,
                minWidth: 0,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 200px' },
                gap: 2,
                alignItems: 'start',
                backgroundColor: '#fff',
                boxShadow: '0 18px 40px rgba(0, 0, 0, 0.08)',
              }}
            >
            <Box>
              <Typography variant="h4" component="h1">
                Purchase Overview
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                Purchasing this kit is a multi-step process. Here is what to expect before you complete your purchase.
              </Typography>

              <Box sx={{ mt: 1.5 }}>
                <List sx={{ p: 0 }}>
                  {steps.map((step, index) => (
                    <ListItem
                      key={step.title}
                      sx={{
                        alignItems: 'flex-start',
                        px: 0,
                        py: 0.5,
                      }}
                    >
                      <ListItemText
                        primary={`Step ${index + 1}: ${step.title}`}
                        secondary={step.description}
                        primaryTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
                        secondaryTypographyProps={{ variant: 'body2', sx: { mt: 0.1 } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Card
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  border: '1px solid #97999B',
                  boxShadow: 'none',
                  width: '100%',
                  borderRadius: 2,
                  alignSelf: 'start',
                }}
              >
                <CardMedia
                  component="img"
                  image={product.image}
                  alt={product.imageAlt}
                  sx={{
                    width: '100%',
                    height: 120,
                    objectFit: 'cover',
                    borderBottom: '1px solid #97999B',
                  }}
                />
                <CardContent sx={{ py: 1.25, px: 1.75 }}>
                  <Typography variant="subtitle2" component="p">
                    Selected kit
                  </Typography>
                  <Typography variant="h6" component="h2" sx={{ mt: 0.25 }}>
                    {product.name}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    First-in-class, blood-based assay quantifying hyperphosphorylated tau for early Alzheimer's detection; verified against tau-PET with over 96% agreement.
                  </Typography>
                </CardContent>
              </Card>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                    boxShadow: '0 12px 22px rgba(0, 118, 127, 0.28)',
                    py: 1.1,
                  }}
                  component={RouterLink}
                  to="/mock/customer-intake"
                >
                  Create account
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  sx={{
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                    borderWidth: 2,
                    py: 1,
                    '&:hover': {
                      borderWidth: 2,
                    },
                  }}
                  component={RouterLink}
                  to="/mock/customer-intake"
                >
                  Sign in
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
